import useSWRInfinite from 'swr/infinite'
import {
  LibraryCollectionItem,
  LibraryCollectionResourcesPage,
  LibraryResourceKind,
} from '@digital-twins/shared-types'

import { LibraryCollectionResourcesQuery, libraryCollectionsApi } from '@/lib/api'

import { CollectionResourceFilters } from './useCollectionFilters'

/** `GET /library/collections/:token/resources`: facet filters are repeated params, one per token. */
export type { LibraryCollectionResourcesQuery }

export const LIBRARY_COLLECTION_RESOURCES_SWR_KEY = 'library/collection-resources'

type ResourcesPageKey = [string, string, LibraryResourceKind, string, string | null]

/** A stable cache key part for the filters: each list sorted, so the order a token was picked in never matters. */
export function collectionResourceFiltersKey(filters: CollectionResourceFilters): string {
  return JSON.stringify([
    [...filters.market].sort(),
    [...filters.year].sort(),
    [...filters.brand].sort(),
  ])
}

/** The resources query of one page: only the set filters, and the cursor past the first page. */
export function toCollectionResourcesQuery(
  kind: LibraryResourceKind,
  filters: CollectionResourceFilters,
  cursor: string | null,
): LibraryCollectionResourcesQuery {
  return {
    kind,
    ...(filters.market.length > 0 ? { market: filters.market } : {}),
    ...(filters.year.length > 0 ? { year: filters.year } : {}),
    ...(filters.brand.length > 0 ? { brand: filters.brand } : {}),
    ...(cursor ? { cursor } : {}),
  }
}

export interface UseCollectionResourcesResult {
  items: Array<LibraryCollectionItem>
  isLoading: boolean
  isLoadingMore: boolean
  hasError: boolean
  hasMore: boolean
  loadMore: () => void
  refresh: () => Promise<unknown>
}

/**
 * One kind's section of a collection under the filters, a page at a time
 * (`useSWRInfinite`, cursor paging), keyed by token, kind and filters. A
 * filter change starts again from the first page. A null token reads
 * nothing.
 */
export function useCollectionResources(
  token: string | null,
  kind: LibraryResourceKind,
  filters: CollectionResourceFilters,
): UseCollectionResourcesResult {
  const filtersKey = collectionResourceFiltersKey(filters)
  const { data, error, isLoading, isValidating, size, setSize, mutate } =
    useSWRInfinite<LibraryCollectionResourcesPage>(
      (pageIndex: number, previous: LibraryCollectionResourcesPage | null): ResourcesPageKey | null => {
        if (!token) {
          return null
        }
        if (pageIndex > 0 && !previous?.nextCursor) {
          return null
        }
        return [LIBRARY_COLLECTION_RESOURCES_SWR_KEY, token, kind, filtersKey, previous?.nextCursor ?? null]
      },
      (key: ResourcesPageKey) =>
        libraryCollectionsApi.listResources(key[1], toCollectionResourcesQuery(key[2], filters, key[4])),
      {
        revalidateOnFocus: false,
        revalidateFirstPage: false,
        shouldRetryOnError: false,
      },
    )

  const pages = data ?? []
  const lastPage = pages.length > 0 ? pages[pages.length - 1] : null
  const hasMore = Boolean(lastPage?.nextCursor)
  const isLoadingMore = isValidating && size > pages.length

  return {
    items: pages.flatMap((page) => page.items),
    isLoading: Boolean(token) && isLoading,
    isLoadingMore,
    hasError: Boolean(error),
    hasMore,
    loadMore: () => {
      if (hasMore && !isLoadingMore) {
        void setSize(size + 1)
      }
    },
    refresh: () => mutate(),
  }
}
